/**
 * HTML do Quill (vue2-editor) -> Markdown.
 *
 * O corpo da campanha e armazenado e renderizado como MARKDOWN nos quatro
 * clientes (V2 com react-markdown sem rehype-raw, legado e preview do Master
 * com markdown-it `html:false`). Todos eles mostram tag crua como TEXTO. Se o
 * Master gravasse o HTML do Quill, o cliente exibiria `<p><strong>x</strong></p>`
 * literalmente. Por isso a conversao acontece aqui, na saida do editor.
 *
 * Percorre o DOM em vez de usar regex: regex sobre HTML erra com aspas simples,
 * entidade e atributo fora de ordem — e aqui o texto vem de um editor que gera
 * markup aninhado.
 *
 * So converte o que a BARRA restrita do editor consegue produzir. Qualquer
 * outra tag vira o seu texto puro, nunca markup solto.
 */

/** Mesma allow-list dos quatro renderizadores. Destino invalido perde o link. */
const SAFE_URL = /^(https?:\/\/|\/(?!\/)|#|mailto:)/i;

/** Escapa o que o Markdown interpretaria como marcacao dentro de texto comum. */
const escapeMarkdown = (text) =>
  text.replace(/([\\`*_[\]])/g, '\\$1');

const isBlank = (text) => !text || !text.trim();

function inline(node) {
  if (node.nodeType === 3) {
    // Quill usa NBSP para espaco de recuo; vira espaco normal.
    return escapeMarkdown(node.nodeValue.replace(/ /g, ' '));
  }

  if (node.nodeType !== 1) return '';

  const tag = node.tagName.toLowerCase();
  const inner = children(node);

  switch (tag) {
    case 'strong':
    case 'b':
      return isBlank(inner) ? inner : '**' + inner.trim() + '**';

    case 'em':
    case 'i':
      return isBlank(inner) ? inner : '*' + inner.trim() + '*';

    case 'a': {
      const href = node.getAttribute('href') || '';
      // Link com destino barrado perde o link, mas NAO perde o texto.
      if (!SAFE_URL.test(href)) return inner;
      return '[' + (inner.trim() || href) + '](' + href + ')';
    }

    // Imagem nao existe no corpo do aviso: o editor ja a barra, e isto garante
    // que nenhuma chega ao texto gravado por outro caminho.
    case 'img':
      return '';

    case 'br':
      return '\n';

    // Sublinhado, cor, fonte e alinhamento nao existem em Markdown. O texto
    // sobrevive; a formatacao cai. E o preco conhecido de manter Markdown.
    default:
      return inner;
  }
}

function children(node) {
  let out = '';
  for (let i = 0; i < node.childNodes.length; i += 1) {
    out += inline(node.childNodes[i]);
  }
  return out;
}

function block(node, lines) {
  if (node.nodeType === 3) {
    const text = escapeMarkdown(node.nodeValue.replace(/ /g, ' '));
    if (!isBlank(text)) lines.push(text.trim());
    return;
  }

  if (node.nodeType !== 1) return;

  const tag = node.tagName.toLowerCase();

  if (tag === 'ul' || tag === 'ol') {
    let index = 1;
    for (let i = 0; i < node.children.length; i += 1) {
      const li = node.children[i];
      if (li.tagName.toLowerCase() !== 'li') continue;
      const text = children(li).trim();
      if (!text) continue;
      lines.push(tag === 'ol' ? index + '. ' + text : '- ' + text);
      index += 1;
    }
    lines.push('');
    return;
  }

  if (/^h[1-6]$/.test(tag)) {
    const text = children(node).trim();
    if (text) {
      lines.push('#'.repeat(Number(tag.charAt(1))) + ' ' + text);
      lines.push('');
    }
    return;
  }

  if (tag === 'blockquote') {
    const text = children(node).trim();
    if (text) {
      lines.push('> ' + text);
      lines.push('');
    }
    return;
  }

  if (tag === 'p' || tag === 'div') {
    const text = children(node).trim();
    // <p><br></p> e a linha vazia do Quill: vira separador, nao texto.
    lines.push(text);
    lines.push('');
    return;
  }

  // Elemento solto na raiz (ex.: <strong> sem <p> em volta).
  const text = inline(node).trim();
  if (text) {
    lines.push(text);
    lines.push('');
  }
}

export const htmlToMarkdown = (html) => {
  if (!html || typeof document === 'undefined') return '';

  const holder = document.createElement('div');
  holder.innerHTML = html;

  const lines = [];
  for (let i = 0; i < holder.childNodes.length; i += 1) {
    block(holder.childNodes[i], lines);
  }

  return lines
    .join('\n')
    // No maximo uma linha em branco entre blocos.
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

export default htmlToMarkdown;



// WEBPACK FOOTER //
// ./src/vue/pages/restricted/broadcast/broadcastHtmlToMarkdown.js